import { inject, Injectable } from '@angular/core';
import { Session, User } from '@supabase/supabase-js';
import { BehaviorSubject, Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { buildMicrosoftAuthorizeUrl, getBrowserOrigin } from '../helpers/app-url.helper';
import { SupabaseService } from './supabase.service';

/** Azure/Supabase authentication and session management service. */
@Injectable({ providedIn: 'root' })
export class AuthService {
  private supabase = inject(SupabaseService);
  private sessionSubject = new BehaviorSubject<Session | null>(null);
  private loadingSubject = new BehaviorSubject<boolean>(true);

  readonly session$ = this.sessionSubject.asObservable();
  readonly user$: Observable<User | null> = new Observable((subscriber) => {
    const sub = this.session$.subscribe((s) => subscriber.next(s?.user ?? null));
    return () => sub.unsubscribe();
  });
  readonly loading$ = this.loadingSubject.asObservable();

  /** Initializes the session and listens for authentication state changes. */
  constructor() {
    this.supabase.auth.getSession().then(({ data }) => {
      this.sessionSubject.next(data.session);
      this.loadingSubject.next(false);
    });

    this.supabase.auth.onAuthStateChange((_event, session) => {
      this.sessionSubject.next(session);
      this.loadingSubject.next(false);
    });
  }

  /** Access to the current session. */
  get session(): Session | null {
    return this.sessionSubject.value;
  }

  /** Access to the authentication loading state. */
  get loading(): boolean {
    return this.loadingSubject.value;
  }

  /** Access to the current user. */
  get user(): User | null {
    return this.sessionSubject.value?.user ?? null;
  }

  /** Redirects to Microsoft Azure for OAuth sign-in. */
  signInWithAzure(returnTo = '/'): void {
    const redirectUri = `${environment.supabaseUrl}/functions/v1/azure-callback`;
    const nonce = crypto.randomUUID();
    sessionStorage.setItem('oauth_nonce', nonce);
    localStorage.setItem('oauth_nonce', nonce);

    const state = btoa(JSON.stringify({ origin: getBrowserOrigin(), returnTo, nonce }));
    const authUrl = new URL(buildMicrosoftAuthorizeUrl(environment.azureTenantId));
    authUrl.searchParams.set('client_id', environment.azureClientId);
    authUrl.searchParams.set('response_type', 'code');
    authUrl.searchParams.set('redirect_uri', redirectUri);
    authUrl.searchParams.set('response_mode', 'query');
    authUrl.searchParams.set('scope', 'openid email profile User.Read');
    authUrl.searchParams.set('state', state);
    window.location.href = authUrl.toString();
  }

  /** Verifies the OAuth nonce and cleans the return URL. */
  verifyOAuthNonce(): boolean {
    const params = new URLSearchParams(window.location.search);
    const returned = params.get('oauth_nonce');
    if (!returned) return true;

    const expected = sessionStorage.getItem('oauth_nonce') ?? localStorage.getItem('oauth_nonce');
    sessionStorage.removeItem('oauth_nonce');
    localStorage.removeItem('oauth_nonce');

    params.delete('oauth_nonce');
    const clean = window.location.pathname + (params.toString() ? `?${params.toString()}` : '') + window.location.hash;
    window.history.replaceState({}, '', clean);
    return expected !== null && expected === returned;
  }

  /** Signs out the user and redirects to the login page. */
  async signOut(): Promise<void> {
    await this.supabase.auth.signOut();
    window.location.href = '/auth/login';
  }

  /** Exchanges the token_hash returned by azure-callback for a Supabase session. */
  async completeMagicLinkSignIn(tokenHash: string): Promise<{ error: { message: string } | null }> {
    const { error } = await this.supabase.auth.verifyOtp({
      token_hash: tokenHash,
      type: 'magiclink',
    });
    if (error) return { error };

    const { data } = await this.supabase.auth.getSession();
    this.sessionSubject.next(data.session);
    this.loadingSubject.next(false);
    return { error: null };
  }
}
